import React, { useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useQuizState } from './hooks/useQuizState';
import { quizQuestions } from './data/quizData';
import { QuizIntro } from './components/QuizIntro';
import { QuizQuestion } from './components/QuizQuestion';
import { LoadingScreen } from './components/LoadingScreen';
import { ResultsPage } from './components/ResultsPage';
import { OfferPage } from './components/OfferPage';
import { Dashboard } from './pages/Dashboard';
import { VSLPage } from './pages/VSLPage';
type AppRoute = 'quiz' | 'dash' | 'lp1';
function detectRoute(): AppRoute {
  if (typeof window === 'undefined') return 'quiz';
  const path = window.location.pathname;
  const search = window.location.search;
  const hash = window.location.hash;
  if (path === '/dash' || path === '/dash/' || search.includes('view=dash') || hash === '#/dash') {
    return 'dash';
  }
  if (path === '/lp1' || path === '/lp1/' || search.includes('view=lp1') || hash === '#/lp1') {
    return 'lp1';
  }
  return 'quiz';
}
export function App() {
  const [route, setRoute] = useState<AppRoute>(detectRoute);
  useEffect(() => {
    const handlePopState = () => setRoute(detectRoute());
    window.addEventListener('popstate', handlePopState);
    // Also listen for hashchange
    window.addEventListener('hashchange', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
      window.removeEventListener('hashchange', handlePopState);
    };
  }, []);
  // Dev navigation helper
  const DevNav = () => <div className="fixed bottom-4 right-4 z-50 flex gap-2 opacity-0 hover:opacity-100 transition-opacity duration-300" data-id="element-0">
      <button onClick={() => {
      window.location.hash = '';
      setRoute('quiz');
    }} className="bg-black/80 text-white text-xs px-2 py-1 rounded border border-white/20" data-id="element-1">
        Quiz
      </button>
      <button onClick={() => {
      window.location.hash = '/lp1';
      setRoute('lp1');
    }} className="bg-black/80 text-white text-xs px-2 py-1 rounded border border-white/20" data-id="element-2">
        LP1
      </button>
      <button onClick={() => {
      window.location.hash = '/dash';
      setRoute('dash');
    }} className="bg-black/80 text-white text-xs px-2 py-1 rounded border border-white/20" data-id="element-3">
        Dash
      </button>
    </div>;
  if (route === 'dash') return <>
        <Dashboard data-id="element-4" />
        <DevNav data-id="element-5" />
      </>;
  if (route === 'lp1') return <>
        <VSLPage data-id="element-6" />
        <DevNav data-id="element-7" />
      </>;
  return <>
      <QuizApp data-id="element-8" />
      <DevNav data-id="element-9" />
    </>;
}
function QuizApp() {
  const {
    currentStep,
    currentQuestionIndex,
    playerType,
    rootCode,
    painTag,
    commitLevel,
    primaryGoal,
    recommendedOffer,
    startQuiz,
    answerQuestion,
    goToResults,
    goToOffer,
    handleCTAClick
  } = useQuizState();
  // Safe fallbacks
  const resolvedType = playerType || 'OBSERVADOR_ATRAPADO';
  const resolvedCode = rootCode || 'MENTE';
  // Determine what to render based on step
  const {
    key,
    content
  } = useMemo(() => {
    // Intro
    if (currentStep === 'intro') {
      return {
        key: 'intro',
        content: <QuizIntro onStart={startQuiz} data-id="element-10" />
      };
    }
    // Quiz questions
    if (currentQuestionIndex !== null && currentQuestionIndex >= 1 && currentQuestionIndex <= quizQuestions.length) {
      const question = quizQuestions[currentQuestionIndex - 1];
      return {
        key: `q-${currentQuestionIndex}`,
        content: <QuizQuestion question={question} totalQuestions={quizQuestions.length} onAnswer={answerQuestion} data-id="element-11" />
      };
    }
    // Loading
    if (currentStep === 'loading') {
      return {
        key: 'loading',
        content: <LoadingScreen onComplete={goToResults} data-id="element-12" />
      };
    }
    // Results
    if (currentStep === 'results') {
      return {
        key: 'results',
        content: <ResultsPage playerType={resolvedType} rootCode={resolvedCode} onViewOffer={goToOffer} data-id="element-13" />
      };
    }
    // Offer
    if (currentStep === 'offer') {
      return {
        key: 'offer',
        content: <OfferPage playerType={resolvedType} rootCode={resolvedCode} painTag={painTag} commitLevel={commitLevel} primaryGoal={primaryGoal} recommendedOffer={recommendedOffer} onCTAClick={handleCTAClick} data-id="element-14" />
      };
    }
    // Fallback
    return {
      key: 'fallback',
      content: <OfferPage playerType={resolvedType} rootCode={resolvedCode} painTag={painTag} commitLevel={commitLevel} primaryGoal={primaryGoal} recommendedOffer={recommendedOffer} onCTAClick={handleCTAClick} data-id="element-15" />
    };
  }, [currentStep, currentQuestionIndex, resolvedType, resolvedCode, painTag, commitLevel, primaryGoal, recommendedOffer, startQuiz, answerQuestion, goToResults, goToOffer, handleCTAClick]);
  return <div className="min-h-screen bg-matrix-bg" data-id="element-16">
      <AnimatePresence mode="sync" data-id="element-17">
        <motion.div key={key} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.35
      }} data-id="element-18">
          {content}
        </motion.div>
      </AnimatePresence>
    </div>;
}